import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DESKTOP_COLUMN } from '../../lib/layout';
import { supabase } from '../../lib/supabase';
import UnitSelect from '../../components/UnitSelect';
import { useAuth } from '../../components/AuthContext';
import { sendNotification, getRequestApproverIds } from '../../lib/notify';
import { orEveryoneWhoCanDecide } from '../../lib/decisionMakers';
import { notifyRequestSubmitted } from '../../lib/materialRequestEmails';
import { getQtyOnHand, getRequesterDepartmentId, createRequestInOneStep } from '../../lib/materialRequests';
import OnboardingCallout from '../../components/OnboardingCallout';
import Overlay from '../../components/Overlay';
import { hasSeenCallout, markCalloutSeen } from '../../lib/userPreferences';
import { workOrderRefError, WORK_ORDER_REF_HINT } from '../../lib/workOrderRef';
import {
  IconSearch,
  IconShoppingCartPlus,
  IconX,
  IconChevronRight,
  IconPhoto,
  IconCalendar,
  IconLock,
  IconCheck,
  IconAlertTriangle,
  IconArrowLeft,
} from '@tabler/icons-react';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

// UOM_OPTIONS retired at Session 78n2, #398 - the last of the four hardcoded
// copies. See components/UnitSelect.js.

function getDateChipValue(chip) {
  const today = new Date();
  if (chip === 'today') return today.toISOString().split('T')[0];
  if (chip === 'tomorrow') {
    const d = new Date(today); d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }
  if (chip === 'thisweek') {
    const d = new Date(today); d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  }
  return null;
}

function formatDateDisplay(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Search result card ─────────────────────────────────────
function CatalogResultCard({ item, stock, onSelect }) {
  return (
    <button
      onClick={() => onSelect(item, stock)}
      style={{ width: '100%', background: 'white', border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer' }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
        <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 2 }}>{item.uom}{item.inventory_categories?.name ? ' · ' + item.inventory_categories.name : ''}</div>
      </div>
      {stock && (
        <div style={{ fontSize: 12, fontWeight: 700, color: stock.qty_on_hand <= 0 ? '#A32D2D' : '#1D9E75', flexShrink: 0 }}>
          {stock.qty_on_hand} on hand
        </div>
      )}
      <IconChevronRight size={16} color="#C0C0C0" />
    </button>
  );
}

// ── Submit error banner ────────────────────────────────────
// The section header here used to read "Catalog item qty selector (for catalog
// path)" and covered CatalogItemForm, which #224 removed at 74t - the catalog path
// now hands off to the shared cart instead of submitting from this page.
//
// #261. The server-error state, and it is deliberately a different shape from the
// per-field validation line that sits in the same place. Validation means "fix this
// field"; this means "the save failed and nothing was recorded". A requester can tell
// them apart at a glance, which a second red line would not have allowed. Module level
// rather than nested, per the locked rule against defining components inside a parent's
// body. Mockup approved by Colby at 74s.
function SubmitErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{ background: '#FCEBEB', border: '1px solid #E24B4A', borderRadius: 10, padding: '12px 13px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <IconAlertTriangle size={17} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#A32D2D', marginBottom: 3 }}>Request not sent</div>
        <div style={{ fontSize: 12, color: '#791F1F', lineHeight: 1.45 }}>{message}</div>
      </div>
    </div>
  );
}


// #444, Session 78o. ONE definition of the four steps, used by the desktop right
// panel and by the mobile sheet below it. Previously the desktop panel owned this
// list inline and mobile had nothing; two copies would drift the way getStatus()
// drifted into four disagreeing versions (src/lib/stockStatus.js).
const WHAT_HAPPENS_NEXT = [
  { n: 1, title: 'Request submitted', desc: 'Your request is logged with a unique MR# and sent to your branch manager.' },
  { n: 2, title: 'Manager reviews', desc: 'Your manager approves or denies the request. You will be notified either way.' },
  // #684, Session 79.22b.4 -- the one added line Colby approved: a made item
  // now goes to the branch that makes it, so step 3 stops saying every request
  // ends in Purchasing.
  { n: 3, title: 'Purchasing orders', desc: 'Approved requests go to purchasing to source, price, and place the order with the vendor. Something D&R makes goes to the branch that makes it instead.' },
  { n: 4, title: 'You receive it', desc: 'When the order arrives, purchasing confirms receipt and updates stock. You are notified.' },
];

const WHAT_HAPPENS_NEXT_KEY = 'request_what_happens_next';

// #444 — the mobile half of the explainer, restored as a bottom sheet.
//
// The desktop right panel has carried these four steps since the page was first
// written; the mobile branch returns a different tree and never had them, so the
// reader most likely to be raising a request — a field team member on a phone at
// a job site — was the one never told what happens to it.
//
// DISMISSAL IS DELIBERATELY ASYMMETRIC, and this is the whole design rather than
// a detail. Only the acknowledge BUTTON writes the dismissal. Escape and a
// backdrop tap close the sheet for this visit and it returns next time the page
// is opened. That is #292's rule: the #148 migration silently added two
// accidental permanent opt-outs — Escape and a backdrop click both wrote the
// dismissal — and somebody who taps the dark area to get a better look at the
// form has not read anything and should not lose it forever.
function WhatHappensNextSheet({ onAcknowledge, onCloseForNow }) {
  return (
    <Overlay variant="sheet" onClose={onCloseForNow}>
      <div style={{ padding: '20px 18px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>
          What happens next
        </div>
        <div style={{ fontSize: 13, color: '#7A7A7A', lineHeight: 1.5, marginBottom: 18 }}>
          You are not ordering this yourself. Here is where it goes.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {WHAT_HAPPENS_NEXT.map(step => (
            <div key={step.n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EF9F27', color: 'white', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {step.n}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>{step.title}</div>
                <div style={{ fontSize: 13, color: '#7A7A7A', lineHeight: 1.5 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onAcknowledge}
          style={{ width: '100%', marginTop: 22, padding: 16, borderRadius: 12, fontSize: 15, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}
        >
          Got it
        </button>
        <div style={{ textAlign: 'center', fontSize: 11.5, color: '#9A9A9A', marginTop: 10 }}>
          Tap <b>Got it</b> and this will not show again.
        </div>
      </div>
    </Overlay>
  );
}

// ── Free-text form (off-catalog path) ─────────────────────
function FreeTextForm({ branchName, profile, onSubmit, onBack, submitting, submitDone, onDone, serverError, photoFailed }) {
  const [description, setDescription] = useState('');
  const [vendorText, setVendorText] = useState('');
  const [vendorMatched, setVendorMatched] = useState(null);
  const [vendorSearching, setVendorSearching] = useState(false);
  const [qty, setQty] = useState(1);
  const [uom, setUom] = useState('each');
  const [woRef, setWoRef] = useState('');
  const [woLocked, setWoLocked] = useState(false);
  const [dateChip, setDateChip] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPhotoNudge, setShowPhotoNudge] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const vendorDebounceRef = useRef(null);

  const neededBy = dateChip === 'custom' ? customDate : getDateChipValue(dateChip);
  const descCount = description.length;
  const descOk = descCount >= 20;

  function handleVendorChange(val) {
    setVendorText(val);
    setVendorMatched(null);
    if (vendorDebounceRef.current) clearTimeout(vendorDebounceRef.current);
    if (!val.trim()) return;
    vendorDebounceRef.current = setTimeout(async () => {
      setVendorSearching(true);
      const { data } = await supabase.from('inventory_vendors')
        .select('id, name').ilike('name', `%${val}%`).eq('is_active', true).limit(1);
      if (data && data.length > 0) setVendorMatched(data[0]);
      setVendorSearching(false);
    }, 400);
  }

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function handleSubmitAttempt() {
    if (!description.trim()) { setError('Please describe what you need.'); return; }
    if (!photo && !showPhotoNudge) { setShowPhotoNudge(true); return; }
    doSubmit();
  }

  function doSubmit() {
    setShowPhotoNudge(false);
    if (!qty || parseInt(qty) < 1) { setError('Please enter a valid quantity.'); return; }
    // #502, Session 79.1 - THE ROOT CAUSE, CAUGHT WHERE THE NUMBER IS TYPED.
    //
    // material_requests.work_order_ref carries NO format rule while
    // inventory_transactions does. So a request could be saved holding "WO-1234",
    // travel all the way to a delivery, and arrive at a receive sheet that COPIES
    // that number in and locks it - leaving the receiver holding a pallet, a
    // read-only field and a database that will refuse the row.
    //
    // Refusing it here is the fix that stops the whole chain rather than rescuing
    // its last link. The receive sheets unlock an invalid inherited number as well,
    // because rows already carrying one exist and this validator cannot reach them.
    const woError = workOrderRefError(woRef);
    if (woError) { setError(woError); return; }
    setError('');
    onSubmit({
      isOffCatalog: true,
      description: description.trim(),
      vendorText: vendorText.trim(),
      vendorMatchedId: vendorMatched?.id || null,
      qty: parseInt(qty),
      uom,
      woRef,
      neededBy: neededBy || null,
      photo,
    });
  }

  if (submitDone) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center' }}>
        <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#2E7D32', marginBottom: 4 }}>Request submitted</div>
          <div style={{ fontSize: 13, color: '#5C5C5C', lineHeight: 1.5 }}>Your manager has been notified and will source and order this item.</div>
        </div>
        {/* #497 - the request is saved and the photo is not. Before Session 79.1
            this path swallowed the upload error entirely, so somebody who
            photographed the part they needed was told "Request submitted" and had
            no way to know the buyer would open a description with no picture. */}
        {photoFailed && (
          <div style={{ background: '#FAEEDA', border: '1.5px solid #EF9F27', borderRadius: 12, padding: '12px 14px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left' }}>
            <IconAlertTriangle size={16} color="#633806" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#633806' }}>Your photo did not upload</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>
                The request is submitted - do not send it again. Only the photo is missing, so
                tell your manager what it looks like or send the picture another way.
              </div>
            </div>
          </div>
        )}
        <button onClick={onDone} style={{ width: '100%', padding: 16, borderRadius: 12, fontSize: 15, fontWeight: 700, border: 'none', background: '#1D9E75', color: 'white', cursor: 'pointer' }}>Done</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 16px 40px' }}>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer', color: '#7A7A7A', fontSize: 13 }}>
        <IconArrowLeft size={15} /> Back to search
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#FFF8EC', color: '#EF9F27', border: '1px solid #EF9F27' }}>Not in catalog</span>
        <span style={{ fontSize: 12, color: '#7A7A7A' }}>Describe what you need</span>
      </div>

      {/* Photo zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{ border: '1.5px dashed #D0D0D0', borderRadius: 12, padding: '16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: photoPreview ? 'transparent' : '#FAFAFA' }}
      >
        {photoPreview ? (
          <img src={photoPreview} alt="preview" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 48, height: 48, borderRadius: 10, background: '#F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconPhoto size={22} color="#C0C0C0" />
          </div>
        )}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#3C3C3C' }}>{photoPreview ? 'Photo attached' : 'Add a photo'}</div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>Helps purchasing source the right item</div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
      </div>

      {/* Description */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>
        Description <span style={{ color: descOk ? '#1D9E75' : '#C0C0C0', fontWeight: 400, textTransform: 'none' }}>
          {descOk ? '✓' : `${descCount}/20 min`}
        </span>
      </div>
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Describe the item — size, color, material, part number if known..."
        style={{ width: '100%', minHeight: 88, border: '1px solid #E0E0E0', borderRadius: 10, padding: '10px 12px', color: '#0F0F0F', resize: 'vertical', marginBottom: 16, boxSizing: 'border-box' }}
      />

      {/* Vendor suggestion */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Suggested vendor (optional)</div>
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <input
          type="text"
          value={vendorText}
          onChange={e => handleVendorChange(e.target.value)}
          placeholder="e.g. Grimco, Fellers..."
          style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box' }}
        />
        {vendorMatched && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <IconCheck size={13} color="#1D9E75" />
            <span style={{ fontSize: 12, color: '#1D9E75', fontWeight: 600 }}>Matched: {vendorMatched.name}</span>
          </div>
        )}
      </div>

      {/* Qty + UOM */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>Qty needed</div>
          <input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)}
            style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>UOM</div>
          {/* Stocking units only. A field user saying what they need is
              answering "what is one of these?" - not how the vendor packages it,
              which nobody at this end of the process knows yet. UnitSelect hides
              the "+ Add a unit" option from them automatically: canAddUnit is
              false for a team member. */}
          <UnitSelect
            kind="measure"
            value={uom}
            onChange={setUom}
            placeholder="pick one"
            ariaLabel="What one of this is"
            style={{ borderRadius: 10, padding: '0 12px' }}
          />
        </div>
      </div>

      {/* WO# */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 6 }}>WO# (optional)</div>
      <div style={{ position: 'relative', marginBottom: 16 }}>
        {/* #502 — the submit message is cleared as soon as the number becomes
            valid. Without this, correcting the field leaves a red line reading
            "…isn't a valid work order number" sitting under a field that now IS
            valid, which is a message contradicting the screen it is on. Caught by
            typing the fix and looking, not by reading the code. */}
        <input type="text" value={woRef} onChange={e => { if (!woLocked) { const v = e.target.value; setWoRef(v); if (error && !workOrderRefError(v)) setError(''); } }}
          placeholder="e.g. 123456 or 123456-7"
          style={{ width: '100%', height: 40, border: workOrderRefError(woRef) ? '1.5px solid #A32D2D' : '1px solid #E0E0E0', borderRadius: 10, padding: woLocked ? '0 36px 0 12px' : '0 12px', color: '#0F0F0F', background: woLocked ? '#F5F5F3' : 'white', boxSizing: 'border-box' }} />
        {woRef && (
          <button onClick={() => setWoLocked(l => !l)}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
            <IconLock size={15} color={woLocked ? '#EF9F27' : '#C0C0C0'} />
          </button>
        )}
        {/* #502 - said beside the field, as they type, in the shop's own words. */}
        {workOrderRefError(woRef) && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: '#A32D2D', marginTop: 5 }}>
            <IconAlertTriangle size={11} color="#A32D2D" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{WORK_ORDER_REF_HINT}</span>
          </div>
        )}
      </div>

      {/* Date needed */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', marginBottom: 8 }}>Needed by (optional)</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: dateChip === 'custom' ? 10 : 16 }}>
        {[
          { id: 'today', label: 'Today' },
          { id: 'tomorrow', label: 'Tomorrow' },
          { id: 'thisweek', label: 'This week' },
          { id: 'custom', label: 'Pick date' },
        ].map(chip => (
          <button key={chip.id}
            onClick={() => setDateChip(dateChip === chip.id ? null : chip.id)}
            style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, border: dateChip === chip.id ? '1.5px solid #EF9F27' : '1px solid #E0E0E0', background: dateChip === chip.id ? '#FFF8EC' : 'white', color: dateChip === chip.id ? '#EF9F27' : '#5C5C5C', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            {chip.id === 'custom' && <IconCalendar size={12} />}{chip.label}
          </button>
        ))}
      </div>
      {dateChip === 'custom' && (
        <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
          style={{ width: '100%', height: 40, border: '1px solid #E0E0E0', borderRadius: 10, padding: '0 12px', color: '#0F0F0F', marginBottom: 16, boxSizing: 'border-box' }} />
      )}
      {dateChip && dateChip !== 'custom' && neededBy && (
        <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 16 }}>{formatDateDisplay(neededBy)}</div>
      )}

      {error && <div style={{ color: '#A32D2D', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {/* Photo nudge */}
      {showPhotoNudge && (
        <div style={{ background: '#FFF8EC', border: '1px solid #EF9F27', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <IconAlertTriangle size={16} color="#EF9F27" />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#9A5F00' }}>No photo attached</span>
          </div>
          <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 12 }}>A photo helps purchasing find the exact item. You can still submit without one.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowPhotoNudge(false); fileInputRef.current?.click(); }}
              style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1px solid #EF9F27', background: 'white', color: '#EF9F27', cursor: 'pointer' }}>
              Add photo
            </button>
            <button onClick={doSubmit}
              style={{ flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', background: '#EF9F27', color: 'white', cursor: 'pointer' }}>
              Submit anyway
            </button>
          </div>
        </div>
      )}

      <SubmitErrorBanner message={serverError} />

      {!showPhotoNudge && (
        // #261. On a retry the button goes straight to doSubmit rather than back through
        // handleSubmitAttempt, so the photo nudge is not shown a second time to someone
        // who has already answered it and is only retrying a failed save.
        <button onClick={serverError ? doSubmit : handleSubmitAttempt} disabled={submitting}
          style={{ width: '100%', padding: 16, borderRadius: 12, fontSize: 15, fontWeight: 700, border: 'none', background: submitting ? '#E0E0E0' : '#EF9F27', color: submitting ? '#9A9A9A' : 'white', cursor: submitting ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Submitting...' : serverError ? 'Try again' : 'Submit request'}
        </button>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function RequestMaterialsPage({ setActive, setScanItemId, setBatchRequestItems }) {
  const { profile, isTeamMember } = useAuth();
  const [branchId, setBranchId] = useState(null);
  const [branchName, setBranchName] = useState('');

  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const [mode, setMode] = useState('search'); // 'search' | 'catalog_form' | 'freetext'

  const [submitting, setSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  // #497 - the request saved but its photo did not upload.
  const [submitPhotoFailed, setSubmitPhotoFailed] = useState(false);
  const [serverError, setServerError] = useState(null);

  // First-run callouts gate, Wave 1 — inv_request_first. The bespoke
  // FirstUseCallout that stood here was a three-step numbered sheet with a
  // "Got it" button: gate decision D1 rules out a tour at any length, D2
  // retires the second implementation, and D8 sets the one-sentence rule.
  // Anchored to the page title, which renders in every mode of this page —
  // the search-panel heading does not, and an anchor that can vanish is #38's
  // silent failure mode.
  const pageTitleRef = useRef(null);

  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (profile?.branch_id) {
      setBranchId(profile.branch_id);
      setBranchName(BRANCHES.find(b => b.id === profile.branch_id)?.name || '');
    }
  }, [profile]);

  // #292's property is now structural rather than maintained. That issue was
  // the migration silently making Escape and a backdrop click permanent,
  // database-backed opt-outs; the fix was routing onClose away from the write.
  // OnboardingCallout has no backdrop and no Escape handler, so the dismiss
  // button is the only thing that can ever write. Nothing to keep in step.

  // Search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!search.trim() || !branchId) { setResults([]); setSearched(false); return; }
    searchDebounceRef.current = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from('inventory_items')
        .select(`
          id, name, sku, uom,
          inventory_categories ( id, name ),
          inventory_stock ( id, qty_on_hand, branch_id )
        `)
        .ilike('name', `%${search}%`)
        .eq('is_active', true)
        .limit(10);

      const withStock = (data || []).map(item => {
        const stock = (item.inventory_stock || []).find(s => s.branch_id === branchId) || null;
        return { item, stock };
      });
      setResults(withStock);
      setSearched(true);
      setSearching(false);
    }, 350);
  }, [search, branchId]);

  // #224. A real catalog hit no longer opens this page's own thin single-item form.
  // It hands off into the shared cart in InventoryActionCatalog, which is entry path
  // 5 in MOCKUP_REFERENCE_212.md Screen 2 - so this page finally gets the reorder_qty
  // prefill, batching and the Search catalog / Scan item affordances it never had.
  //
  // This reuses the exact handoff InventoryStockHome.js already performs for its own
  // single-item path (openRestockModal): null the batch, set the scan item, navigate.
  // Nulling batchRequestItems matters - App.js holds both, and a stale batch from a
  // previous visit would otherwise win over this single item.
  //
  // #684, Session 79.22b.4 -- this passes the default source, NOT Stock Home's
  // 'buy', and that is the fix: the request screen then asks the category tree,
  // and a frame Houston makes opens "Ask Houston to make these" rather than a
  // purchase request. Nobody here has chosen to buy anything yet.
  function handleSelectCatalogItem(item) {
    setBatchRequestItems(null);
    setScanItemId(item.id);
    setActive('inv_request');
  }

  function handleNoneOfThese() {
    setMode('freetext');
  }

  function handleBack() {
    setMode('search');
    setSubmitDone(false);
    setSubmitPhotoFailed(false);
    setServerError(null);
  }

  // #224, 74t. FreeTextForm is now the only caller, so formData.isOffCatalog is
  // always true and every `: formData.item...` branch below is unreachable. They are
  // left standing deliberately rather than stripped in the same commit as the fork:
  // they run through the two-step write, the manager notification and the requester
  // email, and removing them is a separate change that deserves its own live test
  // rather than riding one that could not be exercised here. Logged as follow-up.
  //
  // Do not read those branches as evidence that a catalog item can still be
  // submitted from this page. It cannot - handleSelectCatalogItem navigates away.
  async function handleSubmit(formData) {
    if (!profile || !branchId) return;
    setSubmitting(true);
    setServerError(null);
    try {
      // #497, Session 79.1 - THE HALF THIS PATH ALREADY GOT RIGHT, AND THE HALF IT
      // DID NOT.
      //
      // Right: a failed photo does not throw. The request completes, the work is
      // kept, and only the evidence is lost. That behaviour is what settled the
      // design argument for the RECEIVE path, which used to discard everything.
      //
      // Wrong: it never SAID so. The error was destructured away entirely, so a
      // field member who attached a photo of the part they need was shown "Request
      // submitted" and walked away believing the picture was on the record. The
      // buyer then opens a request describing a bracket with no picture of it.
      // Reported now, in the same words the receive sheet uses.
      let photoUrl = null;
      let photoFailed = false;
      if (formData.photo) {
        const ext = formData.photo.name.split('.').pop();
        const path = `material-requests/${profile.id}/${Date.now()}.${ext}`;
        const { data: uploadData, error: upErr } = await supabase.storage
          .from('inventory-assets')
          .upload(path, formData.photo, { upsert: false });
        if (upErr || !uploadData) {
          photoFailed = true;
          console.error('#497 request photo upload failed, request continuing without it', upErr);
        }
        if (uploadData) {
          const { data: urlData } = supabase.storage
            .from('inventory-assets')
            .getPublicUrl(path);
          photoUrl = urlData?.publicUrl || null;
        }
      }

      // #501, Session 79.1 — STAMP THE REQUESTER'S DEPARTMENT. This insert did not,
      // and the catalog path always has.
      //
      // What it did to a person. A field member in Production asks for something
      // that is not in the catalog. The request saved with no department, so
      // canApprove.js read the blank as "this requester has no department" — a
      // correct rule firing on a false premise — and routed it to the branch
      // manager alone. The department manager never saw it at all.
      //
      // That is the worst class of request to lose: off-catalog is an item nobody
      // has bought, a vendor nobody has used, and no agreed price, which is exactly
      // where the manager who knows the job should be asked. It also quietly loads
      // the person #396 exists to relieve — every off-catalog request at Dallas
      // went straight to the backstop.
      //
      // Read through the shared lookup rather than taken from `profile`, because
      // that lookup exists to stop this: the department is STAMPED at the moment
      // the request is raised, so moving somebody between departments next year
      // cannot re-route what they asked for last year. Oracle puts the cost centre
      // on the requisition for the same reason.
      const departmentId = await getRequesterDepartmentId(profile.id);

      // #352, Session 79.22b.6.1g -- the request and its line are saved in ONE
      // database step (createRequestInOneStep), so a failed line can no longer
      // leave an empty request behind. That retires #261's compensating delete
      // and its "could not be cleaned up" message: there is nothing left to
      // clean up. The department is still read above for the alert's routing;
      // the request itself is stamped inside the database step.
      const itemRow = formData.isOffCatalog
        ? {
            item_id: null,
            qty_requested: formData.qty,
            is_off_catalog: true,
            off_catalog_description: formData.description,
            off_catalog_uom: formData.uom,
            off_catalog_vendor_name: formData.vendorText || null,
            off_catalog_vendor_id: formData.vendorMatchedId || null,
            photo_url: photoUrl,
            needed_by: formData.neededBy || null,
          }
        : {
            item_id: formData.item.id,
            qty_requested: formData.qty,
            is_off_catalog: false,
            notes: formData.notes || null,
          };

      // #261's rule stands: the notification is the app's claim that something
      // happened, so it fires only after the save. With one step, a failed save
      // throws before anything exists, and "Nothing was saved" is simply true.
      const reqData = await createRequestInOneStep({
        branchId,
        workOrderRef: formData.woRef,
        notes: formData.notes,
        lines: [itemRow],
      });

      // #261 second half, 74s. Both writes have succeeded by this point, so the request
      // genuinely exists and is already visible in the manager's inbox. A failure while
      // notifying must therefore NOT be reported to the requester as a failed submit:
      // before this, any throw down here produced an error state on a request that was
      // saved, and the retry it invites would create a real duplicate. Notification
      // failures are logged and swallowed; the write is the source of truth, not the
      // announcement of it.
      try {
        // #501 — THE NOTIFICATION HALF, and stamping the department without this
        // would have been a half fix that looked complete.
        //
        // With the department recorded, canApprove() now renders the Approve button
        // for the department manager. This line decided who gets TOLD, and it was
        // still every branch manager and nobody else — so the person who could now
        // act would never have heard about it. notify.js says so in as many words:
        // the approver query and canApprove() "must agree... if they disagree,
        // somebody is notified about a request they cannot action, or can action
        // one they were never told about."
        //
        // getRequestApproverIds() is the same lookup the catalog path uses, so
        // off-catalog and catalog requests now reach the same two people.
        // #650 -- nobody at the branch who can approve asks the admins and
        // executives instead of telling nobody.
        const { ids: managerIds } = await orEveryoneWhoCanDecide(await getRequestApproverIds(branchId, departmentId), branchId);
        const title = formData.isOffCatalog
          ? `New off-catalog request from ${profile.full_name}`
          : `Restock request: ${formData.item.name}`;
        const message = formData.isOffCatalog
          ? `${profile.full_name} requested an off-catalog item at ${branchName}: "${formData.description}". Request ${reqData.request_number}.`
          : `${profile.full_name} requested ${formData.qty} ${formData.item.uom} of ${formData.item.name} at ${branchName}. Request ${reqData.request_number}.`;

        await sendNotification(
          managerIds,
          title,
          message,
          formData.isOffCatalog ? 'off_catalog_request' : 'restock_request',
          'inventory',
          'requests_inbox',
          reqData.id
        );

        await notifyRequestSubmitted({
          branchId,
          branchName,
          requestNumber: reqData.request_number,
          requesterName: profile.full_name,
          isOffCatalog: formData.isOffCatalog,
          itemName: formData.isOffCatalog ? null : formData.item.name,
          description: formData.isOffCatalog ? formData.description : null,
          qty: formData.qty,
          uom: formData.isOffCatalog ? formData.uom : formData.item.uom,
          // #218. This is the third and last submission path; leaving it out would have left
          // the issue half-fixed. Off-catalog has no catalog item and therefore no on-hand count.
          qtyOnHand: formData.isOffCatalog ? null : await getQtyOnHand(formData.item.id, branchId),
          vendorSuggestion: formData.isOffCatalog ? (formData.vendorText || null) : null,
          photoUrl,
          // #126 — the same reqData.id the sendNotification() above passes as link_id.
          requestId: reqData.id,
        });
      } catch (notifyErr) {
        console.error('#261 request saved but notification failed:', reqData.request_number, notifyErr);
      }

      setSubmitPhotoFailed(photoFailed);
      setSubmitDone(true);
    } catch (err) {
      console.error('Submit error:', err);
      // #261 second half, 74s. Previously this was the whole handler: a console line the
      // requester never saw, a re-enabled button, and no other signal - so the plausible
      // next action was to press it again.
      setServerError("Nothing was saved and your manager wasn't notified. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleDone() {
    setActive?.('inv_home');
  }

  const isMobile = window.innerWidth <= 768;

  // #444 — the mobile explainer. Two pieces of state on purpose:
  // `whatsNextAcked` is the PERMANENT record read from the profile, and
  // `whatsNextOpen` is whether it is on screen right now. A backdrop tap moves
  // only the second; the button moves both. Collapsing them into one would make
  // the tap permanent, which is the #292 defect exactly.
  const [whatsNextOpen, setWhatsNextOpen] = useState(false);
  useEffect(() => {
    if (isMobile && profile && !hasSeenCallout(profile, WHAT_HAPPENS_NEXT_KEY)) {
      setWhatsNextOpen(true);
    }
  }, [isMobile, profile]);

  async function acknowledgeWhatsNext() {
    setWhatsNextOpen(false);
    await markCalloutSeen(profile, WHAT_HAPPENS_NEXT_KEY);
  }

  // ── MOBILE ─────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>

        {whatsNextOpen && (
          <WhatHappensNextSheet
            onAcknowledge={acknowledgeWhatsNext}
            onCloseForNow={() => setWhatsNextOpen(false)}
          />
        )}

        <OnboardingCallout
          calloutKey="inv_request_first"
          text="Search the catalog first. If it is not there, tap &quot;None of these&quot; and describe what you need."
          anchorRef={null}
          isMobile={true}
        />

        {/* Top bar */}
        <div style={{ background: 'white', padding: '12px 16px 12px', borderBottom: '1px solid #E8E8E8', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setActive?.('inv_home')} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <IconArrowLeft size={20} color="#0F0F0F" />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F' }}>Request materials</div>
            <div style={{ fontSize: 11, color: '#7A7A7A' }}>{branchName}</div>
          </div>
          {mode === 'freetext' && (
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#FFF8EC', color: '#EF9F27', border: '1px solid #EF9F27' }}>Not in catalog</span>
          )}
        </div>

        {/* Search state */}
        {mode === 'search' && (
          <div style={{ padding: '14px 14px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'white', border: '1px solid #E0E0E0', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
              <IconSearch size={16} color="#9A9A9A" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search catalog..."
                style={{ border: 'none', background: 'none', outline: 'none', fontSize: 16, color: '#0F0F0F', flex: 1 }}
              />
              {search && (
                <button onClick={() => { setSearch(''); setResults([]); setSearched(false); }}
                  style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer' }}>
                  <IconX size={14} color="#9A9A9A" />
                </button>
              )}
            </div>

            {searching && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>Searching...</div>
            )}

            {!searching && results.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {results.map(({ item, stock }) => (
                  <CatalogResultCard key={item.id} item={item} stock={stock} onSelect={handleSelectCatalogItem} />
                ))}

                {/* Fuzzy match / none of these card */}
                <div style={{ background: '#FAFAFA', border: '1px dashed #D0D0D0', borderRadius: 10, padding: '12px 14px', marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 10 }}>Not finding the right item?</div>
                  <button
                    onClick={handleNoneOfThese}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1.5px solid #EF9F27', background: 'white', color: '#EF9F27', cursor: 'pointer' }}>
                    None of these — describe what I need
                  </button>
                </div>
              </div>
            )}

            {!searching && searched && results.length === 0 && (
              <div style={{ background: 'white', borderRadius: 10, padding: '20px 16px', border: '1px solid #E8E8E8', textAlign: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#3C3C3C', marginBottom: 4 }}>No catalog matches</div>
                <div style={{ fontSize: 12, color: '#9A9A9A', marginBottom: 14 }}>"{search}" isn't in the catalog yet.</div>
                <button onClick={handleNoneOfThese}
                  style={{ padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1.5px solid #EF9F27', background: 'white', color: '#EF9F27', cursor: 'pointer' }}>
                  Describe what I need
                </button>
              </div>
            )}

            {!searching && !searched && (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <IconShoppingCartPlus size={36} color="#D0D0D0" style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A', marginBottom: 4 }}>Search the catalog first</div>
                <div style={{ fontSize: 12, color: '#C0C0C0' }}>Type to search. If nothing matches, you can describe what you need.</div>
              </div>
            )}
          </div>
        )}

        {/* Catalog form */}
        {/* Free-text form */}
        {mode === 'freetext' && (
          <FreeTextForm
            branchName={branchName}
            profile={profile}
            onSubmit={handleSubmit}
            onBack={handleBack}
            submitting={submitting}
            submitDone={submitDone}
            photoFailed={submitPhotoFailed}
            serverError={serverError}
            onDone={handleDone}
          />
        )}
      </div>
    );
  }

  // ── DESKTOP ────────────────────────────────────────────
  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div ref={pageTitleRef} className="page-title">Request materials</div>
          <div className="page-sub">{branchName}</div>
        </div>
        <button className="btn btn-ghost" onClick={() => setActive?.('inv_home')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconArrowLeft size={15} /> Back
        </button>
      </div>

      <OnboardingCallout
        calloutKey="inv_request_first"
        text="Search the catalog first. If it is not there, tap &quot;None of these&quot; and describe what you need."
        anchorRef={pageTitleRef}
        isMobile={false}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: DESKTOP_COLUMN }}>
        {/* Left panel — form */}
        <div className="card" style={{ padding: 24 }}>
          {mode === 'search' && (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F', marginBottom: 12 }}>Search the catalog</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5F5F3', border: '1px solid #E0E0E0', borderRadius: 10, padding: '8px 12px', marginBottom: 16 }}>
                <IconSearch size={16} color="#9A9A9A" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, SKU..."
                  style={{ border: 'none', background: 'none', outline: 'none', fontSize: 16, color: '#0F0F0F', flex: 1 }}
                />
                {search && (
                  <button onClick={() => { setSearch(''); setResults([]); setSearched(false); }}
                    style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer' }}>
                    <IconX size={14} color="#9A9A9A" />
                  </button>
                )}
              </div>

              {searching && <div style={{ color: '#9A9A9A', fontSize: 13 }}>Searching...</div>}

              {!searching && results.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {results.map(({ item, stock }) => (
                    <CatalogResultCard key={item.id} item={item} stock={stock} onSelect={handleSelectCatalogItem} />
                  ))}
                  <div style={{ background: '#FAFAFA', border: '1px dashed #D0D0D0', borderRadius: 10, padding: '12px 14px', marginTop: 4 }}>
                    <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 10 }}>Not finding the right item?</div>
                    <button onClick={handleNoneOfThese}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1.5px solid #EF9F27', background: 'white', color: '#EF9F27', cursor: 'pointer' }}>
                      None of these — describe what I need
                    </button>
                  </div>
                </div>
              )}

              {!searching && searched && results.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#3C3C3C', marginBottom: 4 }}>No catalog matches</div>
                  <div style={{ fontSize: 12, color: '#9A9A9A', marginBottom: 14 }}>"{search}" isn't in the catalog yet.</div>
                  <button onClick={handleNoneOfThese}
                    style={{ padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1.5px solid #EF9F27', background: 'white', color: '#EF9F27', cursor: 'pointer' }}>
                    Describe what I need
                  </button>
                </div>
              )}
            </>
          )}

          {mode === 'freetext' && (
            <FreeTextForm
              branchName={branchName}
              profile={profile}
              onSubmit={handleSubmit}
              onBack={handleBack}
              submitting={submitting}
              submitDone={submitDone}
              photoFailed={submitPhotoFailed}
              serverError={serverError}
              onDone={handleDone}
            />
          )}
        </div>

        {/* Right panel — live preview / what happens next */}
        <div className="card" style={{ padding: 24, background: '#FAFAFA' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 16 }}>What happens next</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {WHAT_HAPPENS_NEXT.map(step => (
              <div key={step.n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EF9F27', color: 'white', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{step.n}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>{step.title}</div>
                  <div style={{ fontSize: 12, color: '#7A7A7A', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {mode === 'freetext' && (
            <div style={{ marginTop: 20, padding: '12px 14px', background: '#FFF8EC', border: '1px solid #EF9F27', borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9A5F00', marginBottom: 4 }}>Off-catalog request</div>
              <div style={{ fontSize: 12, color: '#7A7A7A', lineHeight: 1.5 }}>This item doesn't exist in the catalog yet. If approved and ordered, it's flagged for catalog review so purchasing can add it for future requests.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}